import React, { useEffect, useState } from 'react';
import {
  FaTrash,
  FaRedo,
  FaUserShield,
  FaChalkboardTeacher,
  FaCheckCircle,
  FaArrowLeft,
  FaArrowRight,
} from 'react-icons/fa';
import './users-table.css';
import HintIcon from '../hint-icon/hint-icon';

function UsersTable({ accountData, refreshKey, showAccountName = false }) {
  const PAGE_SIZE = 5;
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(null); // token to fetch the current page
  const [nextCursor, setNextCursor] = useState(null); // token returned for the NEXT page
  const [prevStack, setPrevStack] = useState([]); // stack of tokens to go back
  const [hasMore, setHasMore] = useState(false); // from API
  const currentPage = prevStack.length + 1; // for display only
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [usersToChange, setUsersToChange] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState(''); // State for the filter input

  const toggle = React.useCallback((id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const goNext = () => {
    if (!hasMore) return;
    // Push the token we used to get *this* page, then move cursor to the next
    setPrevStack((stack) => [...stack, cursor]); // cursor can be null on page 1
    setCursor(nextCursor);
  };

  useEffect(() => {
    // new page size → start from the beginning
    setPrevStack([]);
    setCursor(null);
    setNextCursor(null);
  }, [pageSize, refreshKey]);

  useEffect(() => {
    setSelected(new Set());
  }, [cursor, pageSize, refreshKey]);

  const goPrev = () => {
    if (prevStack.length === 0) return;
    setPrevStack((stack) => {
      const nextStack = stack.slice(0, -1);
      const prior = stack[stack.length - 1];
      setCursor(prior); // refetch previous page
      return nextStack;
    });
  };

  const humanDate = (iso) => {
    if (!iso) return 'Never';
    const date = new Date(iso);
    return date.toLocaleString();
  };

  const handleToggle = (idx, user_id, prop) => {
    const found = users.find((u) => u.username === user_id);
    if (found) {
      if (prop === 'administrator' && found.email === accountData.admin_email) {
        console.log('Cannot change admin flag of account super administrator');
        return;
      }

      const nextUsers = users.map((u) =>
        u.username === user_id ? { ...u, [prop]: !u[prop] } : u
      );
      setUsers(nextUsers);

      const updated = nextUsers.find((u) => u.username === user_id);
      if (!updated) return;

      setUsersToChange((prev) => {
        const i = prev.findIndex((u) => u.username === updated.username);
        if (i !== -1) {
          const copy = prev.slice();
          copy[i] = updated;
          return copy;
        }
        return [...prev, updated];
      });
    }
  };

  const loadUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(
        `${process.env.REACT_APP_FACES_API_BASE}/account-users`
      );
      url.searchParams.set('limit', String(pageSize));
      if (cursor) url.searchParams.set('next_token', cursor);

      const res = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
        },
      });

      const data = await res.json();
      const list = Array.isArray(data.users) ? data.users : [];
      setUsers(list);
      setHasMore(Boolean(data.has_more));
      setNextCursor(data.next_token || null);
    } catch {
      setUsers([]);
      setHasMore(false);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  }, [cursor, pageSize]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const displayRows = React.useMemo(() => {
    const list = (users || []).filter(Boolean); // guard against nulls
    const adminEmail = accountData?.admin_email;
    if (!adminEmail) return list;

    const i = list.findIndex((u) => u.email === adminEmail);
    if (i === -1) return list; // owner not in this page

    const owner = list[i];
    const rest = [...list.slice(0, i), ...list.slice(i + 1)];
    return [owner, ...rest]; // owner first, others in original order
  }, [users, accountData?.admin_email]);

  const bulkDelete = async () => {
    // exclude the account owner from deletion
    const emails = [...selected].filter((e) => e !== accountData.admin_email);
    const count = emails.length;
    if (count === 0) return;

    if (
      !window.confirm(
        `Are you sure you want to delete ${count} user${count > 1 ? 's' : ''}? There is no way to undo this.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_FACES_API_BASE}/account-users`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ emails }),
        }
      );

      if (!res.ok) {
        const text = await res.text();
        alert(text || `Delete failed (${res.status})`);
      }

      // Clear selection
      setSelected(new Set());
      await resetToFirstPage();
    } catch (e) {
      console.error(e);
      alert('Failed to delete users: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const deleteSingle = async (id) => {
    if (id === accountData.admin_email) {
      console.log('Cannot delete account owner');
      return;
    }
    if (
      !window.confirm(
        'Are you sure you want to delete this user?  There is no way to undo this.'
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      await fetch(process.env.REACT_APP_FACES_API_BASE + `/account-users`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
          'content-type': 'application/json',
        },
        method: 'DELETE',
        body: JSON.stringify({ email: id }),
      });

      setSelected(new Set());
      await resetToFirstPage();
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const updateUsers = async () => {
    setLoading(true);
    try {
      await fetch(process.env.REACT_APP_FACES_API_BASE + '/account-users', {
        method: 'PUT',
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
        },
        body: JSON.stringify({ users: usersToChange }),
      });

      setUsersToChange([]);
    } catch (e) {
      console.log(e);
      alert('Failed to update users: ' + e);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_FACES_API_BASE}/reset-password`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: accountData.account_id,
            email,
          }),
        }
      );

      if (response.ok) {
        // const data = await response.json();
        alert(`Password reset successfully for ${email}.`);
      } else {
        const error = await response.json();
        alert(`Failed to reset password for ${email}: ${error.message}`);
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('An error occurred while resetting the password.');
    }
  };

  const bulkResetPassword = async () => {
    try {
      const emails = Array.from(selected);
      const response = await fetch(
        `${process.env.REACT_APP_FACES_API_BASE}/reset-password`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            account_id: accountData.account_id,
            email: emails,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(
          `Bulk password reset completed. Summary: ${JSON.stringify(
            data.summary
          )}`
        );
      } else {
        const error = await response.json();
        alert(`Bulk password reset failed: ${error.message}`);
      }
    } catch (error) {
      console.error('Error during bulk password reset:', error);
      alert('An error occurred during the bulk password reset.');
    }
  };

  const resetToFirstPage = async () => {
    const wasFirstPage = prevStack.length === 0 && cursor == null;
    setPrevStack([]);
    setCursor(null);
    setNextCursor(null);
    // If we were already on page 1, manually reload; otherwise the cursor change triggers the effect.
    if (wasFirstPage) {
      await loadUsers();
    }
  };

  const currentUserEmail = localStorage.getItem('facesUserEmail'); // Retrieve current user's email

  const applyFilter = async () => {
    setLoading(true);
    try {
      const url = new URL(
        `${process.env.REACT_APP_FACES_API_BASE}/account-users`
      );
      url.searchParams.set('limit', String(pageSize));
      if (cursor) url.searchParams.set('next_token', cursor);
      if (filter) url.searchParams.set('email', filter);

      const res = await fetch(url.toString(), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('facesIDToken')}`,
        },
      });

      const data = await res.json();
      const list = Array.isArray(data.users) ? data.users : [];
      setUsers(list);
      setHasMore(Boolean(data.has_more));
      setNextCursor(data.next_token || null);
    } catch {
      setUsers([]);
      setHasMore(false);
      setNextCursor(null);
    } finally {
      setLoading(false);
    }
  };

  const resetFilter = () => {
    setFilter('');
    loadUsers();
  };

  return (
    <div style={{ width: '100%', display: 'block', overflowX: 'auto' }}>
      <div
        style={{
          overflowY: users.length > pageSize ? 'auto' : 'visible',
          borderRadius: 8,
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgba(50,56,104,0.06)',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            color: '#23284d',
            background: loading ? '#fff' : undefined,
          }}
        >
          <thead>
            <tr style={{ background: '#f5f6fa' }}>
              <th></th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                }}
              >
                EMAIL
              </th>
              {showAccountName && (
                <th
                  style={{
                    padding: '0.4rem',
                    textAlign: 'left',
                    fontWeight: 600,
                    borderRight: '1px solid #e0e0e0',
                  }}
                >
                  ACCOUNT ID
                </th>
              )}
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                }}
              >
                ACTIVE SINCE
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                }}
              >
                # LOGINS
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'left',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                }}
              >
                USER ID
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                  width: 120,
                  minWidth: 90,
                  maxWidth: 140,
                }}
              >
                ADMINISTRATOR
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                  width: 120,
                  minWidth: 90,
                  maxWidth: 140,
                }}
              >
                INSTRUCTOR
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                  width: 120,
                  minWidth: 90,
                  maxWidth: 140,
                }}
              >
                CERTIFIED
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  borderRight: '1px solid #e0e0e0',
                  width: 120,
                  minWidth: 90,
                  maxWidth: 140,
                }}
              >
                RESET PASSWORD
              </th>
              <th
                style={{
                  padding: '0.4rem',
                  textAlign: 'center',
                  fontWeight: 600,
                  width: 120,
                  minWidth: 90,
                  maxWidth: 140,
                }}
              >
                DELETE
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ background: '#fff' }}>
                <td
                  colSpan={showAccountName ? 7 : 6}
                  style={{
                    textAlign: 'center',
                    padding: '1.2rem',
                    background: '#fff',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 50 50"
                      style={{ marginRight: 8 }}
                    >
                      <circle
                        cx="25"
                        cy="25"
                        r="20"
                        fill="none"
                        stroke="#23284d"
                        strokeWidth="5"
                        strokeDasharray="31.4 31.4"
                        strokeLinecap="round"
                      >
                        <animateTransform
                          attributeName="transform"
                          type="rotate"
                          from="0 25 25"
                          to="360 25 25"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </circle>
                    </svg>
                    Loading users...
                  </span>
                </td>
              </tr>
            ) : (
              displayRows.map((user, idx) => (
                <tr
                  key={user ? user.email : `empty-${idx}`}
                  style={{
                    background:
                      user && user.email === currentUserEmail
                        ? '#e6f7ff' // Light blue tone for the current user
                        : idx % 2 === 0
                          ? '#fff'
                          : '#f0f1f6',
                    color: '#23284d',
                    height: 20,
                    minHeight: 20,
                  }}
                >
                  <td
                    style={{
                      padding: '0.4rem',
                      fontFamily: 'monospace',
                      color: '#23284d',
                      borderRight: '1px solid #e0e0e0',
                      height: 20,
                      minHeight: 20,
                      verticalAlign: 'middle',
                    }}
                  >
                    {user && user.email !== accountData.admin_email && (
                      <input
                        type="checkbox"
                        checked={user && selected.has(user.email)}
                        onChange={() => toggle(user.email)}
                      />
                    )}
                  </td>
                  <td
                    style={{
                      padding: '0.4rem',
                      fontFamily: 'monospace',
                      color: '#23284d',
                      borderRight: '1px solid #e0e0e0',
                      height: 20,
                      minHeight: 20,
                      verticalAlign: 'middle',
                    }}
                  >
                    {user ? user.email : ''}
                  </td>
                  {showAccountName && (
                    <td
                      style={{
                        padding: '0.4rem',
                        fontFamily: 'monospace',
                        color: '#23284d',
                        borderRight: '1px solid #e0e0e0',
                        height: 20,
                        minHeight: 20,
                        verticalAlign: 'middle',
                      }}
                    >
                      {user.account_id}
                    </td>
                  )}
                  <td
                    style={{
                      padding: '0.4rem',
                      fontFamily: 'monospace',
                      color: '#23284d',
                      borderRight: '1px solid #e0e0e0',
                      height: 20,
                      minHeight: 20,
                      verticalAlign: 'middle',
                    }}
                  >
                    {user ? humanDate(user.active_since) : ''}
                  </td>
                  <td
                    style={{
                      padding: '0.4rem',
                      fontFamily: 'monospace',
                      color: '#23284d',
                      borderRight: '1px solid #e0e0e0',
                      height: 20,
                      minHeight: 20,
                      verticalAlign: 'middle',
                    }}
                  >
                    {user ? user.num_logins : ''}
                  </td>
                  <td
                    style={{
                      padding: '0.4rem',
                      fontFamily: 'monospace',
                      color: '#23284d',
                      borderRight: '1px solid #e0e0e0',
                      height: 20,
                      minHeight: 20,
                      verticalAlign: 'middle',
                    }}
                  >
                    {user ? user.username : ''}
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      borderRight: '1px solid #e0e0e0',
                      width: 120,
                      minWidth: 90,
                      maxWidth: 140,
                    }}
                  >
                    {user && (
                      <button
                        onClick={() =>
                          user.email !== accountData.admin_email &&
                          user.email !== currentUserEmail &&
                          handleToggle(idx, user.username, 'administrator')
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor:
                            user.email === accountData.admin_email ||
                            user.email === currentUserEmail
                              ? 'not-allowed'
                              : 'pointer',
                          marginTop: 0,
                          opacity:
                            user.email === accountData.admin_email ||
                            user.email === currentUserEmail
                              ? 0.5
                              : 1, // Fainter color
                        }}
                        disabled={
                          user.email === accountData.admin_email ||
                          user.email === currentUserEmail
                        }
                      >
                        <FaUserShield
                          color={user.administrator ? '#33f' : '#bbb'}
                          title="Toggle admin"
                          size={25}
                        />
                      </button>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      borderRight: '1px solid #e0e0e0',
                      width: 120,
                      minWidth: 90,
                      maxWidth: 140,
                    }}
                  >
                    {user && (
                      <button
                        onClick={() =>
                          user.email !== accountData.admin_email &&
                          handleToggle(idx, user.username, 'instructor')
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor:
                            user.email === accountData.admin_email
                              ? 'not-allowed'
                              : 'pointer',
                          marginTop: 0,
                          opacity:
                            user.email === accountData.admin_email ? 0.5 : 1, // Fainter color
                        }}
                        disabled={user.email === accountData.admin_email}
                      >
                        <FaChalkboardTeacher
                          color={user.instructor ? '#33f' : '#bbb'}
                          title="Toggle Instructor"
                          size={25}
                        />
                      </button>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      borderRight: '1px solid #e0e0e0',
                      width: 120,
                      minWidth: 90,
                      maxWidth: 140,
                    }}
                  >
                    {user && (
                      <button
                        onClick={() =>
                          user.email !== accountData.admin_email &&
                          handleToggle(idx, user.username, 'certified')
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor:
                            user.email === accountData.admin_email
                              ? 'not-allowed'
                              : 'pointer',
                          marginTop: 0,
                          opacity:
                            user.email === accountData.admin_email ? 0.5 : 1, // Fainter color
                        }}
                        disabled={user.email === accountData.admin_email}
                      >
                        <FaCheckCircle
                          color={user.certified ? '#33f' : '#bbb'}
                          title="Toggle Certified"
                          size={25}
                        />
                      </button>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      borderRight: '1px solid #e0e0e0',
                      width: 120,
                      minWidth: 90,
                      maxWidth: 140,
                    }}
                  >
                    {user && (
                      <button
                        onClick={() =>
                          user.email !== accountData.admin_email &&
                          user.email !== currentUserEmail &&
                          handleResetPassword(user.email)
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor:
                            user.email === accountData.admin_email ||
                            user.email === currentUserEmail
                              ? 'not-allowed'
                              : 'pointer',
                          marginTop: 0,
                          opacity:
                            user.email === accountData.admin_email ||
                            user.email === currentUserEmail
                              ? 0.5
                              : 1, // Fainter color
                        }}
                        disabled={
                          user.email === accountData.admin_email ||
                          user.email === currentUserEmail
                        }
                      >
                        <FaRedo color="#27ae60" title="Resend password" />
                      </button>
                    )}
                  </td>
                  <td
                    style={{
                      textAlign: 'center',
                      width: 120,
                      minWidth: 90,
                      maxWidth: 140,
                    }}
                  >
                    {user &&
                      user.email !== accountData.admin_email &&
                      user.email !== currentUserEmail && (
                        <button
                          onClick={() => deleteSingle(user.email)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            marginTop: 0,
                          }}
                        >
                          <FaTrash color="#ba383d" title="Delete user" />
                        </button>
                      )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end', // Adjust alignment to align with inputs
            padding: '0.5rem',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <label
                  htmlFor="filterInput"
                  style={{ fontSize: '1rem', color: '#fff' }}
                >
                  FILTER BY EMAIL:
                </label>
                <HintIcon hint="Email must be an exact match" />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  id="filterInput"
                  type="text"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Email"
                  className="input-field"
                  style={{ margin: 0, width: '100%', maxWidth: '300px' }} // Adjusted for responsiveness
                />
                <button onClick={applyFilter} className="button button-red">
                  SEARCH
                </button>
                <button onClick={resetFilter} className="button button-red">
                  RESET FILTER
                </button>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <label htmlFor="pageSize" style={{ marginRight: '8px' }}>
                PAGE SIZE:
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                }}
                className="page-size-selector"
              >
                {[5, 15, 25, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                className="button page-navigator"
                onClick={goPrev}
                disabled={prevStack.length === 0 || loading}
              >
                <FaArrowLeft />
              </button>
              <span style={{ color: '#fff', fontWeight: 600 }}>
                {currentPage}
              </span>
              <button
                className="button page-navigator"
                onClick={goNext}
                disabled={!hasMore || loading}
              >
                <FaArrowRight />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <button
          onClick={updateUsers}
          className="button"
          style={{ margin: '1.5rem 40px' }}
        >
          <b>SUBMIT USER CHANGES</b>
        </button>
        <button
          onClick={bulkResetPassword}
          disabled={selected.size === 0}
          className="button"
          style={{ margin: '1.5rem 40px' }}
        >
          <b>BULK RESET PASSWORD</b>
        </button>
        <button
          onClick={bulkDelete}
          disabled={selected.size === 0}
          className="button"
          style={{ margin: '1.5rem 40px' }}
        >
          <b>BULK DELETE</b>
        </button>
      </div>
    </div>
  );
}

export default UsersTable;
